import React, { useState } from "react";
import { Alert } from "antd";
import ExcelAiFilesCard from "./ExcelAiFilesCard";
import ExcelAiRunsCard from "./ExcelAiRunsCard";
import ExcelAiRunDetailDrawer from "./ExcelAiRunDetailDrawer";
import { Toast } from "../../ui";
import ExcelAiService from "../../../services/excelAiService";

/**
 * Processamento por IA de uma integração Excel, exibido inline quando o usuário escolhe esse modo.
 *
 * Não é um ERP novo: é um modo de trabalho dentro da integração Excel que já existe. Manda os
 * arquivos como eles são, a IA descobre o formato, normaliza e grava nas mesmas tabelas em que o
 * modelo padrão grava — por isso o aviso de sobrescrita, que não é decorativo.
 */
const ExcelAiPanel = ({ syncId, podeEditar = true }) => {
  const [processando, setProcessando] = useState(false);
  const [sinalAtualizacao, setSinalAtualizacao] = useState(0);
  const [runSelecionado, setRunSelecionado] = useState(null);

  const processar = async () => {
    try {
      setProcessando(true);
      const resultado = await ExcelAiService.processar(syncId);

      if (resultado?.success) {
        Toast.success("Processamento iniciado. Acompanhe abaixo.");
      } else {
        // O caso comum aqui é processamento já em andamento. Não é erro do usuário: é informação.
        Toast.info(resultado?.message || "Não foi possível iniciar o processamento agora.");
      }
      setSinalAtualizacao((n) => n + 1);
    } catch (erro) {
      console.error("Erro ao iniciar processamento:", erro);
      Toast.error("Falha ao iniciar o processamento.");
    } finally {
      setProcessando(false);
    }
  };

  // Sem sync salva não há onde anexar arquivo — o arquivo é gravado contra o id da empresa, que
  // só existe depois do primeiro save. Mostrar um dropzone que falha ao soltar seria pior; o que
  // resolve é dizer que são dois passos e que o primeiro já está ao alcance do botão Salvar.
  if (!syncId) {
    return (
      <Alert
        type="info"
        showIcon
        message="Salve a empresa para liberar o envio das planilhas"
        description={
          <>
            São dois passos: primeiro <b>Salvar</b>, que cria a empresa, e depois o envio das
            planilhas — que ficam guardadas vinculadas a ela. Você não precisa de nenhuma planilha
            agora para salvar.
          </>
        }
      />
    );
  }

  return (
    <>
      {/* Este aviso existe porque a alternativa é o usuário perder dado sem entender por quê:
          os dois modos reconstroem a mesma partição do banco. */}
      <Alert
        type="warning"
        showIcon
        style={{ marginBottom: 20 }}
        message="Cada processamento substitui a carga anterior desta empresa"
        description={
          <>
            Os dados gerados aqui ocupam o mesmo lugar dos que vêm pela planilha no modelo padrão.
            Processar por IA substitui o que foi carregado antes — e processar de novo pelo modelo
            padrão substitui o que a IA carregou. Use um modo de cada vez para a mesma empresa.
          </>
        }
      />

      <ExcelAiFilesCard
        syncId={syncId}
        podeEditar={podeEditar}
        processando={processando}
        onProcessar={processar}
      />

      <ExcelAiRunsCard
        syncId={syncId}
        atualizarSinal={sinalAtualizacao}
        onAbrirDetalhe={setRunSelecionado}
      />

      {/* O detalhe continua em drawer: é vista de aprofundamento, não fluxo principal. */}
      <ExcelAiRunDetailDrawer
        aberto={!!runSelecionado}
        syncId={syncId}
        run={runSelecionado}
        podeEditar={podeEditar}
        onFechar={() => setRunSelecionado(null)}
        onReprocessado={() => setSinalAtualizacao((n) => n + 1)}
      />
    </>
  );
};

export default ExcelAiPanel;
